import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, Monitor, Smartphone, ChevronDown, Clock, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { useSideRail } from '@/contexts/SideRailContext';
import { useSessionsList } from '@/hooks/sessions/useSessionsList';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useAskHobbesChats } from '@/hooks/useAskHobbesHistory';
import { QuickSuggestionPills } from '@/components/dashboard/QuickSuggestionPills';
import {
  SessionsChart,
  type DemoChartDataPoint,
  type TimePeriod,
} from '@/components/dashboard/DemosChart';
import { SessionsTable, type DemoTableRow } from '@/components/dashboard/DemosTable';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/layout/PageHeader';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
};

const DATE_PRESETS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
] as const;

type DatePresetValue = (typeof DATE_PRESETS)[number]['value'];

const TIMELINE_PERIOD_DAYS: Record<TimePeriod, number> = {
  '7d': 7,
  '1m': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365,
};

const toMetricTrend = (raw: number | null | undefined): { value: string; tone: 'positive' | 'negative' | 'neutral' } | undefined => {
  if (raw == null) return undefined;
  if (raw === 0) return { value: '0%', tone: 'neutral' };
  return {
    value: `${raw > 0 ? '+' : ''}${raw}%`,
    tone: raw > 0 ? 'positive' : 'negative',
  };
};

// Chart colors — derived from CSS vars at render time for theme support
const DEVICE_COLORS = [
  'hsl(217 91% 60%)',                // desktop — Figma blue
  'hsl(var(--muted-foreground) / 0.55)', // mobile — muted
];

const SOURCE_COLOR_FALLBACKS = [
  'hsl(151 58% 46%)',                // direct / primary source — Figma green
  'hsl(217 91% 60%)',                // search / organic — blue
  'hsl(38 82% 55%)',                 // referral — amber
  'hsl(262 70% 66%)',                // paid / ads — violet
  'hsl(348 74% 62%)',                // social — rose
  'hsl(184 60% 42%)',                // email / campaign — teal
  'hsl(var(--muted-foreground) / 0.55)', // other — muted
];

const sourceColorFor = (source: string, index: number) => {
  const normalized = source.toLowerCase();
  if (normalized.includes('direct')) return SOURCE_COLOR_FALLBACKS[0];
  if (normalized.includes('organic') || normalized.includes('search') || normalized.includes('google')) return SOURCE_COLOR_FALLBACKS[1];
  if (normalized.includes('referral') || normalized.includes('refer')) return SOURCE_COLOR_FALLBACKS[2];
  if (normalized.includes('paid') || normalized.includes('ad') || normalized.includes('utm')) return SOURCE_COLOR_FALLBACKS[3];
  if (normalized.includes('social') || normalized.includes('linkedin') || normalized.includes('twitter') || normalized.includes('x.com')) return SOURCE_COLOR_FALLBACKS[4];
  if (normalized.includes('email') || normalized.includes('newsletter')) return SOURCE_COLOR_FALLBACKS[5];
  return SOURCE_COLOR_FALLBACKS[index % SOURCE_COLOR_FALLBACKS.length];
};

const MiniDonut: React.FC<{
  segments: { value: number; color: string }[];
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
}> = ({ segments, size = 90, strokeWidth = 6, centerLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;

  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle
        cx={center} cy={center} r={radius}
        fill="none" strokeWidth={strokeWidth}
        stroke="hsl(var(--border) / 0.45)"
      />
      {segments.map((seg, i) => {
        const segLen = (seg.value / total) * circumference;
        const offset = accumulated;
        accumulated += segLen;
        if (seg.value === 0) return null;
        return (
          <circle
            key={i}
            cx={center} cy={center} r={radius}
            fill="none" strokeWidth={strokeWidth}
            stroke={seg.color}
            strokeLinecap="round"
            strokeDasharray={`${segLen} ${circumference - segLen}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        );
      })}
      {centerLabel && (
        <text
          x={center} y={center}
          textAnchor="middle" dominantBaseline="central"
          fill="hsl(var(--muted-foreground))"
          fontSize="14" fontWeight="400"
        >
          {centerLabel}
        </text>
      )}
    </svg>
  );
};

const DashboardPage: React.FC = () => {
  const { userData } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const { openSideRail, setActiveAskHobbesChatId } = useSideRail();
  const [prompt, setPrompt] = useState('');
  const [selectedDays, setSelectedDays] = useState<DatePresetValue>(30);
  const [timelinePeriod, setTimelinePeriod] = useState<TimePeriod>('1m');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const selectedPreset = DATE_PRESETS.find(p => p.value === selectedDays)!;
  const comparisonLabel = `vs prev ${selectedDays}d`;
  const timelineDays = TIMELINE_PERIOD_DAYS[timelinePeriod];

  // Fetch recent sessions for the table only. The timeline uses backend
  // aggregates below so it is not capped by this page size.
  const { sessions, isLoading: isLoadingSessions } = useSessionsList(
    currentOrganization?.id,
    { limit: 100 }
  );

  // Fetch dashboard metrics from the new endpoint
  const { data: dashboardMetrics, isLoading: isLoadingMetrics } = useDashboardMetrics(
    currentOrganization?.id,
    { days: selectedDays }
  );
  const { data: timelineMetrics, isLoading: isTimelineMetricsLoading } = useDashboardMetrics(
    currentOrganization?.id,
    { days: timelineDays }
  );
  const { data: askHobbesChats, isLoading: isLoadingChats } = useAskHobbesChats(currentOrganization?.id);

  const firstName = useMemo(() => {
    const displayName = userData?.displayName?.trim();
    if (displayName) {
      const name = displayName.split(' ')[0];
      if (name) return name.charAt(0).toUpperCase() + name.slice(1);
    }
    const email = userData?.email;
    if (email) {
      const localPart = email.split('@')[0];
      if (localPart) return localPart.charAt(0).toUpperCase() + localPart.slice(1);
    }
    return null;
  }, [userData?.displayName, userData?.email]);

  // Build chart data from backend aggregates rather than the paginated recent
  // sessions list, so 6m/1y ranges are not artificially capped at 100 sessions.
  const chartData: DemoChartDataPoint[] = useMemo(() => {
    const totalSeries = timelineMetrics?.timeSeries?.totalSessions ?? [];
    const qualificationRateSeries = timelineMetrics?.timeSeries?.qualificationRate ?? [];
    if (totalSeries.length === 0) return [];

    const qualificationRateByDate = new Map(
      qualificationRateSeries.map((point) => [point.date, point.value]),
    );

    return totalSeries
      .map((point) => {
        const total = point.value;
        const qualificationRate = qualificationRateByDate.get(point.date) ?? 0;
        const qualified = Math.round(total * (qualificationRate / 100));
        return {
          date: point.date,
          total,
          qualified,
          booked: 0,
          notBooked: Math.max(0, total - qualified),
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [timelineMetrics?.timeSeries?.qualificationRate, timelineMetrics?.timeSeries?.totalSessions]);

  // Transform sessions to table rows (sorted by most recent first)
  const tableData: DemoTableRow[] = useMemo(() => {
    if (!sessions) return [];

    return sessions
      .map(session => ({
        id: session.session_recording_id,
        email: session.email || 'Anonymous',
        qualification_status: session.qualification_status,
        booked_status: session.booked_status,
        role: session.decision_maker_role,
        date: session.created_at,
        duration: session.duration_seconds,
      }))
      .sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  }, [sessions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim()) {
      e.preventDefault();
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSubmit = () => {
    if (prompt.trim()) {
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    navigate(`/ask-hobbes?prompt=${encodeURIComponent(suggestion)}`);
  };

  const handleOpenPastChats = () => {
    openSideRail({ view: 'history' });
  };

  const handleOpenChat = (chatId: string) => {
    setActiveAskHobbesChatId(chatId);
    setHistoryOpen(false);
    openSideRail({ view: 'chat' });
  };

  const recentChats = askHobbesChats?.items.slice(0, 5) ?? [];

  // Pre-compute device breakdown
  const deviceBreakdown = useMemo(() => {
    const desktop = dashboardMetrics?.desktopCount ?? 0;
    const mobile = dashboardMetrics?.mobileCount ?? 0;
    const total = desktop + mobile || 1;
    return {
      desktop,
      mobile,
      total,
      desktopPct: Math.round((desktop / total) * 100),
      mobilePct: Math.round((mobile / total) * 100),
    };
  }, [dashboardMetrics?.desktopCount, dashboardMetrics?.mobileCount]);

  // Pre-compute source breakdown
  const sourceBreakdown = useMemo(() => {
    const sources = (dashboardMetrics?.topSources ?? []).filter((source) => source.count > 0);
    return {
      sources,
      visibleSources: sources.slice(0, 3),
      overflowSources: sources.slice(3),
    };
  }, [dashboardMetrics?.topSources]);

  const isLoading = isLoadingSessions || isLoadingMetrics;

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view the dashboard.</div>;
  }

  return (
    <>
      <Helmet>
        <title>Home | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
      <PageHeader title="Home" />

      {/* Scrollable Content */}
      <div className="min-w-0 flex-1 overflow-y-auto px-4 pb-8 pt-8 sm:px-6 md:pt-10">
        <div className="mx-auto w-full max-w-[960px]">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[24px] font-medium leading-8 text-foreground">
              {firstName ? `${getGreeting()}, ${firstName}` : getGreeting()}
            </h1>
          </div>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
              >
                <span className="truncate text-left">{selectedPreset.label}</span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-2" align="end">
              <div className="flex flex-col gap-1">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent ${
                      selectedDays === preset.value ? 'font-medium bg-accent' : ''
                    }`}
                    onClick={() => {
                      setSelectedDays(preset.value);
                      setDatePickerOpen(false);
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Ask Hobbes Section */}
        <div className="mb-6">
          {/* Ask Hobbes Input Card */}
          <DataSurface className="mb-3 h-[120px] bg-background">
            <div className="px-4 pt-4">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Hobbes anything..."
                className="min-h-[48px] resize-none border-0 bg-transparent p-0 text-[14px] shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between px-4 pb-4 pt-4">
              <div className="flex items-center gap-2">
                <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-[30px] w-[30px] rounded-md text-muted-foreground hover:text-foreground"
                      title="Chats"
                      aria-label="Chats"
                    >
                      <Clock size={14} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" sideOffset={8} className="w-64 rounded-lg p-2">
                    {isLoadingChats ? (
                      <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading chats...
                      </div>
                    ) : recentChats.length === 0 ? (
                      <div className="px-2 py-2 text-sm text-muted-foreground">No chats yet.</div>
                    ) : (
                      <div className="space-y-0.5">
                        {recentChats.map((chat) => (
                          <button
                            key={chat.id}
                            type="button"
                            onClick={() => handleOpenChat(chat.id)}
                            className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                            title={chat.title}
                          >
                            {chat.title}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setHistoryOpen(false);
                        handleOpenPastChats();
                      }}
                      className="mt-1 block w-full rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                    >
                      View all
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
              <Button
                type="button"
                size="icon"
                onClick={handleSubmit}
                disabled={!prompt.trim()}
                className="h-[30px] w-[30px] rounded-full"
              >
                <ArrowUp size={16} />
              </Button>
            </div>
          </DataSurface>
          <QuickSuggestionPills onSuggestionClick={handleSuggestionClick} />
        </div>

        {/* Overview Section */}
        <div className="mb-6 @container">
          {isLoading ? (
            <DataSurface className="grid sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="border-border/70 px-4 py-3 [&:not(:first-child)]:border-t sm:[&:nth-child(even)]:border-l sm:[&:nth-child(even)]:border-t-0 lg:[&:not(:first-child)]:border-l lg:[&:not(:first-child)]:border-t-0">
                  <Skeleton className="mb-3 h-3 w-24" />
                  <Skeleton className="mb-2 h-7 w-16" />
                  <Skeleton className="h-3 w-28" />
                </div>
              ))}
            </DataSurface>
          ) : (
            <MetricStrip
              items={[
                {
                  label: 'Sessions',
                  description: 'Total sessions started in the selected date range.',
                  value: (dashboardMetrics?.totalDemos ?? 0).toLocaleString(),
                  meta: comparisonLabel,
                  trend: toMetricTrend(dashboardMetrics?.totalDemosChange),
                },
                {
                  label: 'Qualified',
                  description: 'Share of sessions that met your qualification criteria.',
                  value: `${dashboardMetrics?.qualificationRate ?? 0}%`,
                  meta: `${dashboardMetrics?.qualifiedCount ?? 0} qualified · ${dashboardMetrics?.unqualifiedCount ?? 0} unqualified`,
                  trend: toMetricTrend(dashboardMetrics?.qualificationRateChange),
                },
                {
                  label: 'Avg length',
                  description: 'Average duration across sessions in the selected date range.',
                  value: formatDuration(dashboardMetrics?.avgSessionLengthSeconds ?? 0),
                  meta: selectedPreset.label,
                  trend: toMetricTrend(dashboardMetrics?.avgSessionLengthChange),
                },
                {
                  label: 'High intent',
                  description: 'Sessions with strong buying signals, such as qualification, meeting intent, or high engagement.',
                  value: (dashboardMetrics?.highIntentLeads ?? 0).toLocaleString(),
                  meta: 'Strong buying signals',
                  trend: toMetricTrend(dashboardMetrics?.highIntentLeadsChange),
                },
              ]}
            />
          )}
        </div>

        {/* Breakdown Cards */}
        {!isLoading && dashboardMetrics && (
          <div className="@container mb-6">
          <div className="grid grid-cols-1 @lg:grid-cols-2 gap-3">
            {/* Device Breakdown */}
            <DataSurface className="h-[182px]">
              <div className="flex h-11 items-center justify-between gap-3 border-b border-border/80 px-4">
                  <TooltipLabel
                    label="Device breakdown"
                    description="Sessions by visitor device type."
                    className="text-sm font-medium text-foreground"
                  />
                  <span className="shrink-0 text-sm text-muted-foreground">{selectedPreset.label}</span>
              </div>
              <div className="flex h-[137px] items-center gap-8 px-4">
                  <MiniDonut
                    centerLabel={deviceBreakdown.total > 1 ? deviceBreakdown.total.toLocaleString() : undefined}
                    segments={[
                      { value: deviceBreakdown.desktop, color: DEVICE_COLORS[0] },
                      { value: deviceBreakdown.mobile, color: DEVICE_COLORS[1] },
                    ]}
                  />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 flex-shrink-0 rounded-[2px]" style={{ backgroundColor: DEVICE_COLORS[0] }} />
                        <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Desktop</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.desktop.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.desktopPct}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 flex-shrink-0 rounded-[2px]" style={{ backgroundColor: DEVICE_COLORS[1] }} />
                        <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Mobile</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.mobile.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.mobilePct}%</span>
                      </div>
                    </div>
                  </div>
              </div>
            </DataSurface>

            {/* Source Breakdown */}
            <DataSurface className="h-[182px]">
              <div className="flex h-11 items-center justify-between gap-3 border-b border-border/80 px-4">
                  <TooltipLabel
                    label="Top sources"
                    description="Where visitors came from before starting a session."
                    className="text-sm font-medium text-foreground"
                  />
                  <span className="shrink-0 text-sm text-muted-foreground">{selectedPreset.label}</span>
              </div>
              <div className="flex h-[137px] items-center px-4">
                {sourceBreakdown.sources.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No traffic sources detected yet. Sources appear once visitors start taking sessions.</p>
                ) : (
                  <div className="flex w-full items-center gap-8">
                    <MiniDonut
                      centerLabel={(dashboardMetrics?.totalDemos ?? 0).toLocaleString()}
                      segments={sourceBreakdown.sources.map((src, i) => ({
                        value: src.count,
                        color: sourceColorFor(src.source, i),
                      }))}
                    />
                    <div className="flex-1 space-y-3">
                      {sourceBreakdown.visibleSources.map((src, i) => (
                        <div key={src.source} className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-[2px]"
                              style={{ backgroundColor: sourceColorFor(src.source, i) }}
                            />
                            <span className="text-sm text-foreground capitalize">{src.source}</span>
                          </div>
                          <span className="text-sm font-medium text-foreground">{src.count.toLocaleString()}</span>
                        </div>
                      ))}
                      {sourceBreakdown.overflowSources.length > 0 && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="flex w-full items-center justify-between gap-4 text-left text-sm text-muted-foreground hover:text-foreground"
                              >
                                <span>More</span>
                                <span className="text-sm font-medium">
                                  +{sourceBreakdown.overflowSources.length}
                                </span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" align="end" className="w-52 p-2.5">
                              <div className="space-y-2">
                                {sourceBreakdown.overflowSources.map((src, overflowIndex) => {
                                  const sourceIndex = overflowIndex + sourceBreakdown.visibleSources.length;
                                  return (
                                    <div key={src.source} className="flex items-center justify-between gap-4">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <span
                                          className="h-2 w-2 flex-shrink-0 rounded-[2px]"
                                          style={{ backgroundColor: sourceColorFor(src.source, sourceIndex) }}
                                        />
                                        <span className="truncate text-xs capitalize text-popover-foreground">
                                          {src.source}
                                        </span>
                                      </div>
                                      <span className="text-xs font-medium tabular-nums text-popover-foreground">
                                        {src.count.toLocaleString()}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </DataSurface>
          </div>
          </div>
        )}

        {/* Chart Section */}
        <div className="mb-6">
          <SessionsChart
            data={chartData}
            period={timelinePeriod}
            onPeriodChange={setTimelinePeriod}
            isLoading={isTimelineMetricsLoading}
          />
        </div>

        {/* Table Section */}
        <SessionsTable sessions={tableData} isLoading={isLoadingSessions} />
        </div>
      </div>
    </div>
    </>
  );
};

export default DashboardPage;
